import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  Loader2,
  Search,
  Layers,
  ShoppingCart,
  UserCheck,
  FileText,
  CreditCard,
  ClipboardList,
  Users,
  Workflow,
  Zap
} from 'lucide-react';

/**
 * Machine Template Gallery
 * 
 * Pre-built templates for common state machine patterns.
 * Reduces time-to-value for new users.
 */

const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: Layers },
  { id: 'auth', name: 'Authentication', icon: UserCheck },
  { id: 'ecommerce', name: 'E-Commerce', icon: ShoppingCart },
  { id: 'workflow', name: 'Workflows', icon: Workflow },
  { id: 'content', name: 'Content', icon: FileText },
  { id: 'cfsp1', name: 'CFSP-1', icon: Workflow },
];

const MACHINE_TEMPLATES = [
  {
    id: 'auth-flow',
    name: 'Authentication Flow',
    description: 'User login, logout, and session management with OAuth support.',
    category: 'auth',
    complexity: 'simple',
    states: 3,
    icon: UserCheck,
    color: 'blue',
    machine_ir: {
      name: 'AuthFlow',
      initial: 'LoggedOut',
      states: {
        LoggedOut: {
          id: 'LoggedOut',
          type: 'atomic',
          transitions: [
            { event: 'LOGIN', target: 'Authenticating', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Authenticating: {
          id: 'Authenticating',
          type: 'atomic',
          transitions: [
            { event: 'AUTH_SUCCESS', target: 'LoggedIn', guards: [], effects: [] },
            { event: 'AUTH_FAILURE', target: 'LoggedOut', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        LoggedIn: {
          id: 'LoggedIn',
          type: 'atomic',
          transitions: [
            { event: 'LOGOUT', target: 'LoggedOut', guards: [], effects: [] },
            { event: 'SESSION_EXPIRED', target: 'LoggedOut', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'checkout-flow',
    name: 'Checkout Flow',
    description: 'E-commerce checkout with cart, payment, and confirmation.',
    category: 'ecommerce',
    complexity: 'medium',
    states: 5,
    icon: ShoppingCart,
    color: 'green',
    machine_ir: {
      name: 'CheckoutFlow',
      initial: 'Cart',
      states: {
        Cart: {
          id: 'Cart',
          type: 'atomic',
          transitions: [
            { event: 'PROCEED_TO_CHECKOUT', target: 'Shipping', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Shipping: {
          id: 'Shipping',
          type: 'atomic',
          transitions: [
            { event: 'SHIPPING_ENTERED', target: 'Payment', guards: [], effects: [] },
            { event: 'BACK', target: 'Cart', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Payment: {
          id: 'Payment',
          type: 'atomic',
          transitions: [
            { event: 'PAYMENT_SUBMITTED', target: 'Processing', guards: [], effects: [] },
            { event: 'BACK', target: 'Shipping', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Processing: {
          id: 'Processing',
          type: 'atomic',
          transitions: [
            { event: 'PAYMENT_SUCCESS', target: 'Confirmed', guards: [], effects: [] },
            { event: 'PAYMENT_FAILED', target: 'Payment', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Confirmed: {
          id: 'Confirmed',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'order-lifecycle',
    name: 'Order Lifecycle',
    description: 'Track orders from placement through delivery and completion.',
    category: 'ecommerce',
    complexity: 'medium',
    states: 7,
    icon: ClipboardList,
    color: 'purple',
    machine_ir: {
      name: 'OrderLifecycle',
      initial: 'Placed',
      states: {
        Placed: {
          id: 'Placed',
          type: 'atomic',
          transitions: [
            { event: 'CONFIRM', target: 'Confirmed', guards: [], effects: [] },
            { event: 'CANCEL', target: 'Cancelled', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Confirmed: {
          id: 'Confirmed',
          type: 'atomic',
          transitions: [
            { event: 'SHIP', target: 'Shipped', guards: [], effects: [] },
            { event: 'CANCEL', target: 'Cancelled', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Shipped: {
          id: 'Shipped',
          type: 'atomic',
          transitions: [
            { event: 'OUT_FOR_DELIVERY', target: 'OutForDelivery', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        OutForDelivery: {
          id: 'OutForDelivery',
          type: 'atomic',
          transitions: [
            { event: 'DELIVER', target: 'Delivered', guards: [], effects: [] },
            { event: 'DELIVERY_FAILED', target: 'DeliveryFailed', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Delivered: {
          id: 'Delivered',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        DeliveryFailed: {
          id: 'DeliveryFailed',
          type: 'atomic',
          transitions: [
            { event: 'RETRY_DELIVERY', target: 'OutForDelivery', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Cancelled: {
          id: 'Cancelled',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'content-approval',
    name: 'Content Approval',
    description: 'Draft, review, and publish workflow for content management.',
    category: 'content',
    complexity: 'medium',
    states: 6,
    icon: FileText,
    color: 'amber',
    machine_ir: {
      name: 'ContentApproval',
      initial: 'Draft',
      states: {
        Draft: {
          id: 'Draft',
          type: 'atomic',
          transitions: [
            { event: 'SUBMIT_FOR_REVIEW', target: 'InReview', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        InReview: {
          id: 'InReview',
          type: 'atomic',
          transitions: [
            { event: 'APPROVE', target: 'Approved', guards: [], effects: [] },
            { event: 'REQUEST_CHANGES', target: 'Draft', guards: [], effects: [] },
            { event: 'REJECT', target: 'Rejected', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Approved: {
          id: 'Approved',
          type: 'atomic',
          transitions: [
            { event: 'PUBLISH', target: 'Published', guards: [], effects: [] },
            { event: 'SCHEDULE', target: 'Scheduled', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Scheduled: {
          id: 'Scheduled',
          type: 'atomic',
          transitions: [
            { event: 'PUBLISH_NOW', target: 'Published', guards: [], effects: [] },
            { event: 'CANCEL_SCHEDULE', target: 'Approved', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Published: {
          id: 'Published',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Rejected: {
          id: 'Rejected',
          type: 'atomic',
          transitions: [
            { event: 'REVISE', target: 'Draft', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'subscription-lifecycle',
    name: 'Subscription Lifecycle',
    description: 'SaaS subscription from trial through active and churn.',
    category: 'ecommerce',
    complexity: 'medium',
    states: 6,
    icon: CreditCard,
    color: 'cyan',
    machine_ir: {
      name: 'SubscriptionLifecycle',
      initial: 'Trial',
      states: {
        Trial: {
          id: 'Trial',
          type: 'atomic',
          transitions: [
            { event: 'CONVERT', target: 'Active', guards: [], effects: [] },
            { event: 'TRIAL_EXPIRED', target: 'Expired', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Active: {
          id: 'Active',
          type: 'atomic',
          transitions: [
            { event: 'PAYMENT_FAILED', target: 'PastDue', guards: [], effects: [] },
            { event: 'CANCEL', target: 'Cancelled', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        PastDue: {
          id: 'PastDue',
          type: 'atomic',
          transitions: [
            { event: 'PAYMENT_RECOVERED', target: 'Active', guards: [], effects: [] },
            { event: 'GRACE_PERIOD_ENDED', target: 'Churned', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Cancelled: {
          id: 'Cancelled',
          type: 'atomic',
          transitions: [
            { event: 'REACTIVATE', target: 'Active', guards: [], effects: [] },
            { event: 'PERIOD_ENDED', target: 'Churned', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Expired: {
          id: 'Expired',
          type: 'atomic',
          transitions: [
            { event: 'CONVERT', target: 'Active', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Churned: {
          id: 'Churned',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'form-wizard',
    name: 'Multi-Step Form',
    description: 'Step-by-step form wizard with validation and navigation.',
    category: 'workflow',
    complexity: 'simple',
    states: 5,
    icon: ClipboardList,
    color: 'indigo',
    machine_ir: {
      name: 'FormWizard',
      initial: 'Step1',
      states: {
        Step1: {
          id: 'Step1',
          type: 'atomic',
          transitions: [
            { event: 'NEXT', target: 'Step2', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: { label: 'Personal Info' }
        },
        Step2: {
          id: 'Step2',
          type: 'atomic',
          transitions: [
            { event: 'NEXT', target: 'Step3', guards: [], effects: [] },
            { event: 'BACK', target: 'Step1', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: { label: 'Contact Details' }
        },
        Step3: {
          id: 'Step3',
          type: 'atomic',
          transitions: [
            { event: 'NEXT', target: 'Review', guards: [], effects: [] },
            { event: 'BACK', target: 'Step2', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: { label: 'Preferences' }
        },
        Review: {
          id: 'Review',
          type: 'atomic',
          transitions: [
            { event: 'SUBMIT', target: 'Complete', guards: [], effects: [] },
            { event: 'BACK', target: 'Step3', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: { label: 'Review & Submit' }
        },
        Complete: {
          id: 'Complete',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: { label: 'Complete' }
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'user-onboarding',
    name: 'User Onboarding',
    description: 'Guide new users through setup with progress tracking.',
    category: 'workflow',
    complexity: 'simple',
    states: 4,
    icon: Users,
    color: 'rose',
    machine_ir: {
      name: 'UserOnboarding',
      initial: 'Welcome',
      states: {
        Welcome: {
          id: 'Welcome',
          type: 'atomic',
          transitions: [
            { event: 'START', target: 'ProfileSetup', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        ProfileSetup: {
          id: 'ProfileSetup',
          type: 'atomic',
          transitions: [
            { event: 'PROFILE_COMPLETE', target: 'FeatureTour', guards: [], effects: [] },
            { event: 'SKIP', target: 'Complete', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        FeatureTour: {
          id: 'FeatureTour',
          type: 'atomic',
          transitions: [
            { event: 'TOUR_COMPLETE', target: 'Complete', guards: [], effects: [] },
            { event: 'SKIP', target: 'Complete', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Complete: {
          id: 'Complete',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'async-task',
    name: 'Async Task Runner',
    description: 'Background task execution with retry and error handling.',
    category: 'workflow',
    complexity: 'medium',
    states: 6,
    icon: Zap,
    color: 'orange',
    machine_ir: {
      name: 'AsyncTaskRunner',
      initial: 'Idle',
      states: {
        Idle: {
          id: 'Idle',
          type: 'atomic',
          transitions: [
            { event: 'START', target: 'Running', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Running: {
          id: 'Running',
          type: 'atomic',
          transitions: [
            { event: 'COMPLETE', target: 'Completed', guards: [], effects: [] },
            { event: 'ERROR', target: 'Failed', guards: [], effects: [] },
            { event: 'CANCEL', target: 'Cancelled', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Failed: {
          id: 'Failed',
          type: 'atomic',
          transitions: [
            { event: 'RETRY', target: 'Running', guards: [], effects: [] },
            { event: 'ABORT', target: 'Aborted', guards: [], effects: [] }
          ],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Completed: {
          id: 'Completed',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Cancelled: {
          id: 'Cancelled',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        },
        Aborted: {
          id: 'Aborted',
          type: 'final',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    }
  },
  {
    id: 'cfsp1-constraint-architecture',
    name: 'CFSP-1 Constraint Architecture',
    description: 'A fully CFSP-1 compliant machine with guards, effects, invariants, and complete coverage. Demonstrates all six constraint strata.',
    category: 'cfsp1',
    complexity: 'advanced',
    states: 6,
    icon: Workflow,
    color: 'cyan',
    machine_ir: {
      name: 'ConstraintArchitecture',
      initial: 'Unconstrained',
      states: {
        Unconstrained: {
          id: 'Unconstrained',
          type: 'atomic',
          transitions: [
            { event: 'DEFINE_ONTOLOGY', target: 'OntologyDefined', guards: [{ name: 'hasStakeholder', predicate: 'hasStakeholder', params: {} }], effects: ['LogConstraintEvent'] }
          ],
          entry: [],
          exit: [],
          invariants: [{ name: 'no_phantom_constraints', assertion: 'constraints.length === 0', message: 'Unconstrained state must have zero active constraints' }],
          meta: { stratum: 'ONTOLOGICAL', cfsp1: true }
        },
        OntologyDefined: {
          id: 'OntologyDefined',
          type: 'atomic',
          transitions: [
            { event: 'MAP_TOPOLOGY', target: 'TopologyMapped', guards: [{ name: 'ontologyComplete', predicate: 'ontologyComplete', params: {} }], effects: ['ValidateCoherence'] },
            { event: 'REVISE_ONTOLOGY', target: 'Unconstrained', guards: [], effects: ['LogConstraintEvent'] }
          ],
          entry: ['InitializeConstraintSpace'],
          exit: [],
          invariants: [{ name: 'axiom_coherence', assertion: 'axioms.coherent === true', message: 'A0.4: Constraint coherence must be maintained' }],
          meta: { stratum: 'TOPOLOGICAL', cfsp1: true }
        },
        TopologyMapped: {
          id: 'TopologyMapped',
          type: 'atomic',
          transitions: [
            { event: 'DEFINE_DYNAMICS', target: 'DynamicsEstablished', guards: [{ name: 'noGaps', predicate: 'coverageGaps === 0', params: {} }], effects: ['ValidateCoherence'] },
            { event: 'COVERAGE_GAP_FOUND', target: 'OntologyDefined', guards: [], effects: ['LogFailureMode'] }
          ],
          entry: ['AnalyzeCoverage'],
          exit: [],
          invariants: [{ name: 'no_unreachable', assertion: 'unreachableStates.length === 0', message: 'F-07: No temporal decay allowed' }],
          meta: { stratum: 'DYNAMICAL', cfsp1: true }
        },
        DynamicsEstablished: {
          id: 'DynamicsEstablished',
          type: 'atomic',
          transitions: [
            { event: 'VERIFY_COHERENCE', target: 'Verified', guards: [{ name: 'controlScoreAboveThreshold', predicate: 'controlScore >= 0.8', params: {} }], effects: ['ComputeControlScore'] },
            { event: 'INCOHERENCE_DETECTED', target: 'TopologyMapped', guards: [], effects: ['LogFailureMode'] },
            { event: 'FALSIFY', target: 'Falsified', guards: [{ name: 'counterexampleFound', predicate: 'counterexampleFound', params: {} }], effects: ['RecordFalsification'] }
          ],
          entry: ['RunCFSP1Analysis'],
          exit: [],
          invariants: [{ name: 'measurable_coherence', assertion: 'controlScore !== undefined', message: 'A0.4: Coherence must be measurable' }],
          meta: { stratum: 'EPISTEMIC', cfsp1: true }
        },
        Verified: {
          id: 'Verified',
          type: 'final',
          transitions: [],
          entry: ['EmitVerificationReport'],
          exit: [],
          invariants: [{ name: 'self_consistent', assertion: 'selfApplicationPasses === true', message: 'CFSP-1 must apply to itself without contradiction' }],
          meta: { stratum: 'STRATEGIC', cfsp1: true }
        },
        Falsified: {
          id: 'Falsified',
          type: 'atomic',
          transitions: [
            { event: 'REVISE_ARCHITECTURE', target: 'Unconstrained', guards: [], effects: ['LogConstraintEvent'] }
          ],
          entry: ['RecordFalsification'],
          exit: [],
          invariants: [{ name: 'falsification_surface_open', assertion: 'canBeFalsified === true', message: 'Theory without falsification surface is not a theory' }],
          meta: { stratum: 'EPISTEMIC', cfsp1: true }
        }
      },
      events: [],
      effects: {
        LogConstraintEvent: { name: 'LogConstraintEvent', input_type: 'ConstraintEvent', output_type: 'void', failure_type: 'never', required_capabilities: [] },
        ValidateCoherence: { name: 'ValidateCoherence', input_type: 'ConstraintSet', output_type: 'CoherenceResult', failure_type: 'IncoherenceError', required_capabilities: [] },
        InitializeConstraintSpace: { name: 'InitializeConstraintSpace', input_type: 'void', output_type: 'ConstraintSpace', failure_type: 'never', required_capabilities: [] },
        AnalyzeCoverage: { name: 'AnalyzeCoverage', input_type: 'ConstraintSpace', output_type: 'CoverageReport', failure_type: 'never', required_capabilities: [] },
        LogFailureMode: { name: 'LogFailureMode', input_type: 'FailureMode', output_type: 'void', failure_type: 'never', required_capabilities: [] },
        ComputeControlScore: { name: 'ComputeControlScore', input_type: 'AnalysisResult', output_type: 'number', failure_type: 'never', required_capabilities: [] },
        RecordFalsification: { name: 'RecordFalsification', input_type: 'Counterexample', output_type: 'void', failure_type: 'never', required_capabilities: [] },
        RunCFSP1Analysis: { name: 'RunCFSP1Analysis', input_type: 'MachineIR', output_type: 'CFSP1Analysis', failure_type: 'AnalysisError', required_capabilities: [] },
        EmitVerificationReport: { name: 'EmitVerificationReport', input_type: 'VerificationData', output_type: 'Report', failure_type: 'never', required_capabilities: [] },
      },
      guards: {
        hasStakeholder: { name: 'hasStakeholder', predicate: 'context.stakeholder !== undefined', params: {} },
        ontologyComplete: { name: 'ontologyComplete', predicate: 'context.ontology.complete === true', params: {} },
        noGaps: { name: 'noGaps', predicate: 'context.coverageGaps === 0', params: {} },
        controlScoreAboveThreshold: { name: 'controlScoreAboveThreshold', predicate: 'context.controlScore >= 0.8', params: {} },
        counterexampleFound: { name: 'counterexampleFound', predicate: 'context.counterexample !== null', params: {} },
      },
      invariants: [],
      meta: { framework: 'CFSP-1', version: '1.0' }
    }
  }
];

const TemplateGallery = ({ open, onOpenChange, onSelectTemplate, isCreating }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const filteredTemplates = MACHINE_TEMPLATES.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate.machine_ir);
    }
  };

  const colorClasses = {
    blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    green: 'bg-green-500/20 border-green-500/30 text-green-400',
    purple: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    amber: 'bg-amber-500/20 border-amber-500/30 text-amber-400',
    cyan: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400',
    indigo: 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400',
    rose: 'bg-rose-500/20 border-rose-500/30 text-rose-400',
    orange: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] bg-[#0a0e27] border-white/10 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="flex items-center space-x-2">
            <Layers className="w-5 h-5 text-blue-400" />
            <span>Template Gallery</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Start with a pre-built template and customize it to your needs.
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-[500px]">
          {/* Sidebar - Categories */}
          <div className="w-48 border-r border-white/10 p-3 flex-shrink-0">
            <div className="space-y-1">
              {TEMPLATE_CATEGORIES.map(category => {
                const Icon = category.icon;
                const count = category.id === 'all' 
                  ? MACHINE_TEMPLATES.length 
                  : MACHINE_TEMPLATES.filter(t => t.category === category.id).length;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors",
                      selectedCategory === category.id
                        ? "bg-blue-500/20 text-blue-400"
                        : "hover:bg-white/5 text-gray-400"
                    )}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon className="w-4 h-4" />
                      <span>{category.name}</span>
                    </div>
                    <span className="text-xs text-gray-600">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-black/30 border-white/10"
                />
              </div>
            </div>

            {/* Template Grid */}
            <ScrollArea className="flex-1 p-4">
              <div className="grid grid-cols-2 gap-4">
                {filteredTemplates.map(template => {
                  const Icon = template.icon;
                  const isSelected = selectedTemplate?.id === template.id;
                  
                  return (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={cn(
                        "text-left p-4 rounded-lg border transition-all",
                        isSelected
                          ? "bg-blue-500/20 border-blue-500/50 ring-2 ring-blue-500/30"
                          : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                      )}
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border",
                          colorClasses[template.color]
                        )}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-white truncate">{template.name}</h3>
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{template.description}</p>
                          <div className="flex items-center space-x-2 mt-2">
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                              {template.states} states
                            </Badge>
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5 capitalize">
                              {template.complexity}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p>No templates match your search.</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-black/20">
          <div className="text-sm text-gray-500">
            {selectedTemplate ? (
              <span>Selected: <strong className="text-white">{selectedTemplate.name}</strong></span>
            ) : (
              <span>Select a template to continue</span>
            )}
          </div>
          <div className="flex space-x-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUseTemplate}
              disabled={!selectedTemplate || isCreating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="use-template-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Use Template'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateGallery;
